import React, { type FC, useEffect, useCallback } from 'react';
import cx from 'classnames';
import {
  TableListItem,
  Text,
  TextButton,
  Button,
  Tooltip,
  Box,
  CheckToggle,
} from '@wix/design-system';
import type { TableListItemColumn } from '@wix/design-system';
import {
  AUTOMATION_IDS,
  getBusinessAppSetupStepDataHook,
} from '../../../constants/automationIds';
import { usePrevious } from '../../../hooks/usePrevious';
import type { AppSetupStep as AppSetupStepModel } from '../../../services/setup/types';
import { useBILogger } from '@wix/fe-essentials/react';
import { TRANSLATIONS } from '../../../constants/translations';
import styles from './AppSetupStep.module.scss';
import { t } from '../../../../../essentials/i18n';
import { BI_EVENTS_IDS, BI_PARAMS } from '../../../constants/biEvents';
import { getBIReportData } from '../../../utils/biEvents';

export interface AppSetupStepProps {
  appId: string;
  step: AppSetupStepModel;
  isPriority: boolean;
  showDivider: boolean;
  biData: {
    biSetupStatus: string;
    stepRank: number;
  };
}

enum ActionType {
  MAIN_ACTION = 'mainAction',
  SKIP = 'skip',
  UNDO = 'undo',
}

export const AppSetupStep: FC<AppSetupStepProps> = ({
  appId,
  step,
  isPriority,
  showDivider,
  biData,
}) => {
  const logger = useBILogger();

  const reportStepActionBiEvent = useCallback(
    (actionType: ActionType) => {
      const targetCta =
        actionType === ActionType.MAIN_ACTION
          ? BI_PARAMS.TARGET_CTA.BUTTON_CLICKED
          : actionType;
      logger.log(
        getBIReportData(BI_EVENTS_IDS.ACTION_CLICK, appId, {
          section: BI_PARAMS.SECTIONS.SETUP,
          action_id: step.id,
          target_cta: targetCta,
          action_rank_on_section: biData.stepRank,
          setup_status: biData.biSetupStatus,
        }),
      );
    },
    [logger, step],
  );

  const onMainAction = useCallback(() => {
    step.action.execute();
    reportStepActionBiEvent(ActionType.MAIN_ACTION);
  }, [reportStepActionBiEvent, step]);

  const onSkip = useCallback(() => {
    step.skip();
    reportStepActionBiEvent(ActionType.SKIP);
  }, [reportStepActionBiEvent, step]);

  const onUnskip = useCallback(() => {
    step.unskip();
    reportStepActionBiEvent(ActionType.UNDO);
  }, [reportStepActionBiEvent, step]);

  const getOptions = (): TableListItemColumn[] => {
    switch (step.status) {
      case 'todo':
        return [
          {
            value: (
              <Box verticalAlign="middle" gap="SP2">
                <Box minWidth="18px" className={styles.checkboxContainer}>
                  <CheckToggle size="small" skin="success" disabled />
                </Box>

                <StepTitle title={step.title} />
              </Box>
            ),
            width: 'auto',
            align: 'left',
          },
          {
            value: (
              <TodoStepActions
                step={step}
                isPriority={isPriority}
                onMainAction={onMainAction}
                onSkip={onSkip}
              />
            ),
            align: 'right',
          },
        ];
      case 'skipped':
        return [
          {
            value: (
              <Box verticalAlign="middle" gap="SP2">
                <Box minWidth="18px" className={styles.checkboxContainer}>
                  <CheckToggle size="small" skin="success" disabled />
                </Box>

                <StepTitle title={step.title} isDimmed />
              </Box>
            ),
            width: 'auto',
            align: 'left',
          },
          {
            value: <SkippedStepActions step={step} onUnskip={onUnskip} />,
            align: 'right',
          },
        ];
      case 'completed':
        return [
          {
            value: (
              <Box verticalAlign="middle" gap="SP2">
                <Box minWidth="18px" className={styles.checkboxContainer}>
                  <CheckToggle
                    size="small"
                    skin="success"
                    checked
                    // input with a checked property requires an onChange handler to avoid react error
                    onChange={(e) => e.target.value}
                  />
                </Box>

                <StepTitle title={step.title} />
              </Box>
            ),
            width: 'auto',
            align: 'left',
          },
        ];
      default:
        throw new Error(`Unexpected setup step status "${step.status}"`);
    }
  };

  const prevStatus = usePrevious(step.status);

  useEffect(() => {
    if (
      step.status === 'completed' &&
      prevStatus !== undefined &&
      step.status !== prevStatus
    ) {
      logger.log(
        getBIReportData(BI_EVENTS_IDS.SETUP_ACTION_DONE, appId, {
          action_id: step.id,
          action_rank_on_section: biData.stepRank,
          setup_status: biData.biSetupStatus,
        }),
      );
    }
  }, [logger, step, prevStatus]);

  return (
    <TableListItem
      className={styles.container}
      dataHook={getBusinessAppSetupStepDataHook(step.id)}
      verticalPadding="medium"
      showDivider={showDivider}
      options={getOptions()}
    />
  );
};

const StepTitle: FC<{
  title: string;
  isDimmed?: boolean;
  isCrossed?: boolean;
}> = ({ title, isDimmed, isCrossed }) => {
  return (
    <div className={cx(styles.title, { [styles.titleCrossed]: isCrossed })}>
      <Text
        dataHook="setup-step-title-text"
        size="small"
        weight="thin"
        skin={isDimmed ? 'disabled' : 'standard'}
        maxLines={2}
        ellipsis
        secondary
      >
        {t(title)}
      </Text>
    </div>
  );
};

const SkippedStepActions: FC<{
  step: AppSetupStepModel;
  onUnskip: () => void;
}> = ({ step, onUnskip }) => {
  return (
    <Box gap="SP2" verticalAlign="middle">
      <Text size="tiny" weight="thin" skin="disabled">
        {t(TRANSLATIONS.MY_BUSINESS.BUSINESS_APP.LINK)}
      </Text>

      <Tooltip
        disabled={step.isPermitted}
        content={t(TRANSLATIONS.MY_BUSINESS.SETUP_STEP.PERMISSION_TEXT)}
      >
        <TextButton
          size="tiny"
          weight="thin"
          disabled={!step.isPermitted}
          dataHook={AUTOMATION_IDS.MY_BUSINESS.APP_SETUP.STEP_UNSKIP}
          onClick={onUnskip}
        >
          {t(TRANSLATIONS.MY_BUSINESS.BUSINESS_APP.BTN_UNDO)}
        </TextButton>
      </Tooltip>
    </Box>
  );
};

const TodoStepActions: FC<{
  step: AppSetupStepModel;
  isPriority: boolean;
  onMainAction: () => void;
  onSkip: () => void;
}> = ({ step, isPriority, onMainAction, onSkip }) => {
  const allowSkip = !step.isMandatory && step.isPermitted;

  return (
    <Box gap="SP2" verticalAlign="middle">
      {allowSkip && (
        <TextButton
          size="tiny"
          weight="thin"
          dataHook={AUTOMATION_IDS.MY_BUSINESS.APP_SETUP.STEP_SKIP}
          onClick={onSkip}
        >
          {t(TRANSLATIONS.MY_BUSINESS.BUSINESS_APP.BTN_SKIP)}
        </TextButton>
      )}

      <Tooltip
        disabled={step.isPermitted}
        content={t(TRANSLATIONS.MY_BUSINESS.SETUP_STEP.PERMISSION_TEXT)}
      >
        <Button
          size="tiny"
          priority={!isPriority && step.isPermitted ? 'secondary' : 'primary'}
          disabled={!step.isPermitted}
          dataHook={AUTOMATION_IDS.MY_BUSINESS.APP_SETUP.STEP_MAIN_ACTION}
          onClick={onMainAction}
        >
          {t(step.action.label)}
        </Button>
      </Tooltip>
    </Box>
  );
};
